'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { HeroDateRangeSearch } from '@/modules/preceptor/components/hero-date-range-search';
import Image from 'next/image';
import { Animated, StaggerContainer, AnimatedCounter } from '@/components/ui/animated';
import { useAuthStore } from '@/store/auth-store';
import type { UserRole } from '@/types/api/auth';

const dashboardHrefForRole = (role?: UserRole): string => {
  switch (role) {
    case 'student':
    default:
      return '/student/bookings';
  }
};

export default function MainLandingPage() {
  const user = useAuthStore((s) => s.user);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const isLoggedIn = isMounted && !!user;
  const primaryCtaHref = isLoggedIn ? dashboardHrefForRole(user?.role) : '/student/np/signup';
  const primaryCtaLabel = isLoggedIn ? 'Rotations' : 'Get Started';

  return (
    <div className="flex flex-col">
      {/* Hero with search */}
  <section className="relative overflow-hidden pt-16 pb-8 md:pt-32 md:pb-44">
        {/* Layered background */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-white via-sky-50 to-white" />
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[1300px] h-[1300px] rounded-full bg-[radial-gradient(circle_at_center,rgba(14,165,233,0.18),transparent_70%)]" />
          <div className="absolute top-1/3 left-0 w-72 h-72 bg-gradient-to-tr from-cyan-100 to-transparent blur-2xl opacity-70" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tr from-blue-100 to-transparent blur-2xl opacity-60" />
        </div>
  <div className="section-inner grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <Animated animation="fade-right" duration={800}>
            <div className="space-y-6 md:space-y-8">
              <div className="space-y-4 md:space-y-5">
                <h1 className="text-3xl sm:text-4xl md:text-[clamp(2rem,4vw,3.5rem)] font-bold tracking-tight leading-[1.15] md:leading-[1.1] text-center md:text-left">
                  Easily Find <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Preceptors</span> for Your Clinical <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Experience</span>
                </h1>
                <p
                  style={{ fontSize: 'clamp(19px, 1vw + 15px, 22px)', lineHeight: 1.6 }}
                  className="text-gray-600 max-w-xl text-center md:text-left mx-auto md:mx-0"
                >
                  Browse our available preceptors to find one that best matches your requirements. The ACE rotations team will help you fulfill your academic goals.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 md:gap-4 justify-center md:justify-start">
                <Link href="/contact">
                  <Button size="lg" className="h-10 px-5 text-sm md:h-12 md:px-8 md:text-base font-semibold shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 hover:scale-105 transition-all">
                    Contact Us
                  </Button>
                </Link>
                <Link href={primaryCtaHref}>
                  <Button size="lg" variant="outline" className="h-10 px-5 text-sm md:h-12 md:px-8 md:text-base font-semibold border-2 hover:bg-gray-50 hover:scale-105 hover:text-[lab(2.75381% 0 0)] transition-all">
                    {primaryCtaLabel}
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap justify-center md:gap-6 md:justify-start">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Transparent scheduling</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Structured evidence</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Mentored growth</span>
                </div>
              </div>
            </div>
          </Animated>
          <Animated animation="fade-left" delay={200} duration={800}>
            <div className="relative z-20">
              <HeroDateRangeSearch className="shadow-2xl shadow-blue-500/10" />
            </div>
          </Animated>
        </div>
      </section>

      {/* Trust Metrics + Partners */}
      <section className="mt-2 mb-8 md:mb-12 md:-mt-14 relative z-10">
        <div className="section-inner flex flex-col gap-10">
          <Animated animation="fade-up" delay={300}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 rounded-xl border border-[var(--brand-border)] bg-white p-6 shadow-sm">
              {[{label:'Preceptors',value:2000,suffix:'+'},{label:'Rotations Managed',value:14,suffix:'k+'},{label:'Satisfaction',value:97,suffix:'%'},{label:'Avg. Response',value:48,prefix:'<',suffix:'h'}].map(m => (
                <div key={m.label} className="text-center space-y-1">
                  <div className="text-2xl font-semibold text-blue-600">
                    <AnimatedCounter end={m.value} prefix={m.prefix} suffix={m.suffix} duration={2000} />
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-[var(--brand-muted)]">{m.label}</div>
                </div>
              ))}
            </div>
          </Animated>
          <Animated animation="fade-up" delay={400}>
            <div className="border border-[var(--brand-border)] bg-white rounded-xl p-4 md:p-6 flex flex-col gap-4 shadow-sm">
              <p className="text-base font-semibold text-gray-700 text-center">Trusted By Partners & Preceptors</p>
              <div className="flex flex-wrap justify-center gap-6 md:gap-10 items-center">
                <a href="https://www.bbb.org/us/ca/west-hollywood/profile/employment-agencies/american-clinical-experience-1216-723884" target="_blank" rel="noopener noreferrer" className="relative w-40 h-20 md:w-48 md:h-24 grayscale hover:grayscale-0 transition-all hover:scale-105">
                  <Image src="/images/american-clinical-experience-723884.png" alt="BBB A+ Accreditation" fill className="object-contain" sizes="(max-width: 768px) 160px, 192px" />
                </a>
                {[ '/images/ACE_LOGO_3+LINES_SHADOWS.webp', '/images/rsz_png_image_2_1.webp' ].map(src => (
                  <div key={src} className="relative w-40 h-20 md:w-48 md:h-24 grayscale hover:grayscale-0 transition-all hover:scale-105">
                    <Image src={src} alt="Partner Logo" fill className="object-contain" sizes="(max-width: 768px) 160px, 192px" />
                  </div>
                ))}
              </div>
            </div>
          </Animated>
        </div>
      </section>

  {/* How It Works Timeline */}
  <section className="pb-16 md:pb-32">
        <div className="section-inner">
          <Animated animation="fade-up">
            <h3 className="text-xl font-semibold tracking-tight mb-8">How It Works</h3>
          </Animated>
          <StaggerContainer staggerDelay={100} animation="fade-up" className="grid md:grid-cols-5 gap-4">
            {[
              {step:'1',title:'Find a preceptor',text:'Find the best preceptor using our map'},
              {step:'2',title:'Details',text:'Enter your rotation details and create your account.'},
              {step:'3',title:'Secure your spot',text:'Authorize deposit hold to send details to the team.'},
              {step:'4',title:'ACE rotations review',text:'The team will manually check your placement and confirm the preceptor.'},
              {step:'5',title:'Paperwork',text:'Your paperwork will be sent to the preceptor for signature.'},
            ].map(s => (
              <div key={s.step} className="h-full border border-[var(--brand-border)] rounded-xl bg-white p-5 flex flex-col gap-2 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="text-sm font-medium text-blue-600">Step {s.step}</div>
                <div className="text-sm font-semibold">{s.title}</div>
                <p className="text-[11px] text-[var(--brand-muted)] leading-relaxed">{s.text}</p>
              </div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="pb-16 md:pb-40">
        <Animated animation="fade-up">
          <div className="max-w-7xl mx-auto px-4 md:px-12">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl p-6 md:p-10 text-center text-white shadow-md">
              <h3 className="text-xl md:text-2xl font-semibold tracking-tight mb-3 md:mb-4">Start Your Rotation Planning</h3>
              <p className="text-sm/6 max-w-xl mx-auto mb-5 md:mb-6 opacity-90">Create an account to start the process, check how we helped previous students or check our frequently asked questions.</p>
              <div className="flex flex-col items-center gap-3 md:flex-row md:flex-wrap md:justify-center md:gap-4">
                <Link href="/auth/student/signup"><Button variant="secondary" className="bg-white text-[var(--brand-primary)] hover:bg-white/90 hover:scale-105 transition-transform">Create Account</Button></Link>
                <div className="flex flex-row gap-3 md:contents">
                  <Link href="/testimonials"><Button variant="outline" className="border-white text-white bg-white/20 hover:bg-white/30 hover:scale-105 transition-transform">Testimonials</Button></Link>
                  <Link href="/faq"><Button variant="outline" className="border-white text-white bg-white/20 hover:bg-white/30 hover:scale-105 transition-transform">FAQ</Button></Link>
                </div>
              </div>
            </div>
          </div>
        </Animated>
      </section>
    </div>
  );
}
